import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowRight, Search, Plus, Book, FileText, Scale, 
  MessageSquare, Filter, ChevronDown, ChevronUp, Trash2,
  BookOpen, Gavel, Building2, ScrollText, LogOut
} from 'lucide-react';
import { Logo } from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categoryIcons = {
  system: ScrollText,
  regulation: FileText,
  precedent: Gavel,
  supreme_court: Building2,
  law_book: Book,
  fiqh_book: BookOpen,
  decision: FileText,
};

const categoryNames = {
  system: 'الأنظمة السعودية',
  regulation: 'اللوائح التنفيذية',
  precedent: 'السوابق القضائية',
  supreme_court: 'قرارات المحكمة العليا',
  law_book: 'كتب القانون',
  fiqh_book: 'كتب الفقه',
  decision: 'القرارات والتعاميم',
};

const subcategoryNames = {
  criminal: 'جنائي',
  commercial: 'تجاري',
  family: 'أحوال شخصية',
  labor: 'عمالي',
  administrative: 'إداري',
  civil: 'مدني',
  real_estate: 'عقاري',
};

export default function LegalLibrary() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    category: 'system',
    subcategory: '',
    content: '',
    summary: '',
    source: '',
    year: '',
    number: '',
    last_update: '',
    keywords: '',
  });

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setUser(JSON.parse(userStr));
    }
    fetchCategories();
    fetchDocuments();
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [selectedCategory, selectedSubcategory]);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/legal-library/categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategories(response.data.categories);
      setSubcategories(response.data.subcategories);
      setStats(response.data.stats);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API}/legal-library/documents?`;
      if (selectedCategory) url += `category=${selectedCategory}&`;
      if (selectedSubcategory) url += `subcategory=${selectedSubcategory}&`;
      if (searchQuery) url += `search=${encodeURIComponent(searchQuery)}&`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDocuments(response.data);
    } catch (err) {
      console.error('Error fetching documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchDocuments();
  };

  const viewDocument = async (docId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/legal-library/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedDoc(response.data);
      setShowModal(true);
    } catch (err) {
      console.error('Error fetching document:', err);
    }
  };

  const handleAddDocument = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        year: formData.year ? parseInt(formData.year) : null,
        keywords: formData.keywords ? formData.keywords.split(',').map(k => k.trim()) : [],
      };
      
      await axios.post(`${API}/legal-library/documents`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setShowAddModal(false);
      setFormData({
        title: '',
        category: 'system',
        subcategory: '',
        content: '',
        summary: '',
        source: '',
        year: '',
        number: '',
        last_update: '',
        keywords: '',
      });
      fetchDocuments();
      fetchCategories();
    } catch (err) {
      console.error('Error adding document:', err);
      alert('حدث خطأ أثناء إضافة المستند');
    }
  };

  const deleteDocument = async (docId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المستند؟')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/legal-library/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchDocuments();
      fetchCategories();
    } catch (err) {
      console.error('Error deleting document:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-sand" dir="rtl" lang="ar">
      <header className="bg-primary py-4 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowRight className="w-6 h-6 text-white" />
            </button>
            <Logo size="md" />
            <h1 className="text-2xl font-heading font-bold text-white">المكتبة القانونية</h1>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/legal-library/ai')}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-all"
              data-testid="ai-assistant-btn"
            >
              <MessageSquare className="w-5 h-5" />
              المساعد الذكي
            </button>
            {user?.role !== 'client' && (
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gold text-white rounded-md hover:bg-gold-dark transition-all"
                data-testid="add-document-btn"
              >
                <Plus className="w-5 h-5" />
                إضافة مستند
              </button>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-all"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              خروج
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* بطاقات الفئات */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
          {categories.map((cat) => {
            const Icon = categoryIcons[cat.id] || FileText;
            return (
              <button
                key={cat.id}
                onClick={() => {
                  setSelectedCategory(selectedCategory === cat.id ? '' : cat.id);
                }}
                className={`p-4 rounded-xl text-center transition-all ${
                  selectedCategory === cat.id
                    ? 'bg-gold text-white shadow-lg scale-105'
                    : 'bg-white text-primary hover:shadow-md hover:scale-102'
                }`}
                data-testid={`category-${cat.id}`}
              >
                <Icon className="w-8 h-8 mx-auto mb-2" />
                <p className="text-sm font-body font-medium">{cat.name}</p>
                <p className={`text-xs mt-1 ${selectedCategory === cat.id ? 'text-white/80' : 'text-gray-500'}`}>
                  {stats[cat.id] || 0} مستند
                </p>
              </button>
            );
          })}
        </div>

        {/* البحث والفلترة */}
        <div className="bg-white rounded-xl p-6 shadow-sm mb-8">
          <form onSubmit={handleSearch} className="flex gap-4 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="ابحث في المكتبة القانونية..."
                className="w-full pr-10 pl-4 py-3 border border-gray-200 rounded-lg font-body focus:ring-2 focus:ring-gold focus:border-transparent"
                data-testid="search-input"
              />
            </div>
            <button
              type="submit"
              className="px-6 py-3 bg-gold text-white rounded-lg font-body hover:bg-gold-dark transition-all"
              data-testid="search-btn"
            >
              بحث
            </button>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-3 border border-gray-200 rounded-lg font-body hover:bg-gray-50 transition-all"
            >
              <Filter className="w-5 h-5" />
              فلترة
              {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </form>

          {showFilters && (
            <div className="flex gap-4 pt-4 border-t border-gray-100">
              <select
                value={selectedSubcategory}
                onChange={(e) => setSelectedSubcategory(e.target.value)}
                className="px-4 py-2 border border-gray-200 rounded-lg font-body"
                data-testid="subcategory-filter"
              >
                <option value="">جميع التخصصات</option>
                {subcategories.map((sub) => (
                  <option key={sub.id} value={sub.id}>{sub.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* قائمة المستندات */}
        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin w-8 h-8 border-4 border-gold border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-500 font-body">جاري تحميل المستندات...</p>
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl">
            <Book className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 font-body text-lg">لا توجد مستندات</p>
            <p className="text-gray-400 font-body text-sm mt-2">
              {selectedCategory ? 'جرب البحث في فئة أخرى' : 'ابدأ بإضافة مستندات للمكتبة'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {documents.map((doc) => {
              const Icon = categoryIcons[doc.category] || FileText;
              return (
                <div
                  key={doc.id}
                  className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all cursor-pointer"
                  onClick={() => viewDocument(doc.id)}
                  data-testid={`document-card-${doc.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-gold/10 rounded-lg">
                      <Icon className="w-6 h-6 text-gold" />
                    </div>
                    <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-body">
                      {categoryNames[doc.category]}
                    </span>
                  </div>
                  <h3 className="text-lg font-heading font-bold text-primary mb-2 line-clamp-2">
                    {doc.title}
                  </h3>
                  {doc.summary && (
                    <p className="text-sm text-gray-600 font-body mb-3 line-clamp-2">
                      {doc.summary}
                    </p>
                  )}
                  <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 font-body">
                    {doc.year && <span>سنة {doc.year}</span>}
                    {doc.number && <span>رقم {doc.number}</span>}
                    {doc.last_update && (
                      <span className="px-2 py-1 bg-green-50 text-green-600 rounded">
                        آخر تحديث: {doc.last_update}
                      </span>
                    )}
                    {doc.subcategory && (
                      <span className="px-2 py-1 bg-blue-50 text-blue-600 rounded">
                        {subcategoryNames[doc.subcategory]}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Modal عرض المستند */}
      {showModal && selectedDoc && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-heading font-bold text-primary mb-2">
                  {selectedDoc.title}
                </h2>
                <div className="flex gap-2">
                  <span className="px-3 py-1 bg-gold/10 text-gold rounded-full text-sm">
                    {categoryNames[selectedDoc.category]}
                  </span>
                  {selectedDoc.subcategory && (
                    <span className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm">
                      {subcategoryNames[selectedDoc.subcategory]}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                {user?.role === 'admin' && (
                  <button
                    onClick={() => {
                      deleteDocument(selectedDoc.id);
                      setShowModal(false);
                    }}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    data-testid="delete-document-btn"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                )}
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-body"
                >
                  إغلاق
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {selectedDoc.summary && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-heading font-bold text-primary mb-2">ملخص</h3>
                  <p className="text-gray-700 font-body">{selectedDoc.summary}</p>
                </div>
              )}
              <div className="prose prose-lg max-w-none font-body text-gray-800 whitespace-pre-wrap">
                {selectedDoc.content}
              </div>
              {selectedDoc.keywords?.length > 0 && (
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <h4 className="font-heading font-bold text-primary mb-2">الكلمات المفتاحية</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedDoc.keywords.map((keyword, idx) => (
                      <span key={idx} className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-6 pt-4 border-t border-gray-100 text-sm text-gray-500 font-body">
                {selectedDoc.source && <p>المصدر: {selectedDoc.source}</p>}
                {selectedDoc.year && <p>سنة الإصدار: {selectedDoc.year}</p>}
                {selectedDoc.number && <p>الرقم: {selectedDoc.number}</p>}
                {selectedDoc.last_update && (
                  <p className="text-green-600 font-medium">آخر تحديث للنظام: {selectedDoc.last_update}</p>
                )}
                <p>أضافه: {selectedDoc.uploaded_by_name}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal إضافة مستند */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-2xl font-heading font-bold text-primary">إضافة مستند جديد</h2>
            </div>
            <form onSubmit={handleAddDocument} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-body text-gray-700 mb-1">عنوان المستند *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg font-body"
                  required
                  data-testid="doc-title-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-body text-gray-700 mb-1">الفئة *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg font-body"
                    required
                    data-testid="doc-category-select"
                  >
                    {Object.entries(categoryNames).map(([id, name]) => (
                      <option key={id} value={id}>{name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-body text-gray-700 mb-1">التخصص</label>
                  <select
                    value={formData.subcategory}
                    onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg font-body"
                    data-testid="doc-subcategory-select"
                  >
                    <option value="">اختر التخصص</option>
                    {Object.entries(subcategoryNames).map(([id, name]) => (
                      <option key={id} value={id}>{name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-body text-gray-700 mb-1">سنة الإصدار</label>
                  <input
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg font-body"
                    placeholder="1445"
                    data-testid="doc-year-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-body text-gray-700 mb-1">رقم النظام/القرار</label>
                  <input
                    type="text"
                    value={formData.number}
                    onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg font-body"
                    data-testid="doc-number-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-body text-gray-700 mb-1">المصدر</label>
                  <input
                    type="text"
                    value={formData.source}
                    onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg font-body"
                    placeholder="هيئة الخبراء"
                    data-testid="doc-source-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-body text-gray-700 mb-1">آخر تحديث</label>
                  <input
                    type="text"
                    value={formData.last_update}
                    onChange={(e) => setFormData({ ...formData, last_update: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg font-body"
                    placeholder="1446/01/15"
                    data-testid="doc-last-update-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-body text-gray-700 mb-1">ملخص</label>
                <textarea
                  value={formData.summary}
                  onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg font-body h-20"
                  data-testid="doc-summary-input"
                />
              </div>
              <div>
                <label className="block text-sm font-body text-gray-700 mb-1">المحتوى *</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg font-body h-40"
                  required
                  data-testid="doc-content-input"
                />
              </div>
              <div>
                <label className="block text-sm font-body text-gray-700 mb-1">الكلمات المفتاحية (مفصولة بفواصل)</label>
                <input
                  type="text"
                  value={formData.keywords}
                  onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg font-body"
                  placeholder="عقد، إيجار، تجاري"
                  data-testid="doc-keywords-input"
                />
              </div>
              <div className="flex gap-4 pt-4">
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-gold text-white rounded-lg font-body hover:bg-gold-dark transition-all"
                  data-testid="save-document-btn"
                >
                  حفظ المستند
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-6 py-3 border border-gray-200 rounded-lg font-body hover:bg-gray-50 transition-all"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
